import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Subject, BehaviorSubject, Observable, pipe, Subscription, zip, of } from 'rxjs';
import { debounceTime, tap, map, distinctUntilChanged, filter, takeUntil, takeLast, merge, switchMap, catchError, share } from "rxjs/operators";
import { DatePipe, DecimalPipe, KeyValuePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatIconModule } from '@angular/material/icon';

import { QuotationsService, RentalChildInvoice, RentalLifecycleView, RentalLineLifecycle } from '@app/sales/quotations/quotations.service';
import { ValuesService } from '@app/common/values/values.service';
import { CommonService } from '@app/common/common.service';
import { Store } from '@app/shared/_models/common';
import { businessDayOf } from '@app/shared/time';
import * as constants from "@app/shared/constants";
import { rentalSubtitle, rentalTitle, statusHint, statusLabel } from '@app/sales/rental-billing/rental-billing.labels';

/** Ligne survolée de la liste F4 : le type de pièce dit s'il faut charger le cycle de location en même temps que la commande. */
export interface HoveredOrder { id: number; doc_type?: string; parent_order_id?: number | null; doc_ref?: string | null; }

// Lien vers la pièce d'origine (texte, lien) : seule une FACTURE fille est une échéance ; le parent d'un avoir est la pièce qu'il annule, celui d'une FAC-AC sa commande.
export function parentOrderLabel(docType?: string | null): [string, string] {
	switch (docType) {
		case constants.DocType.FACTURE:
		case constants.DocType.FACTURE_FSE: return ['Échéance issue du', 'Contrat de location'];
		case constants.DocType.AVOIR: return ['Avoir de la', 'Pièce'];
		case constants.DocType.FACTURE_ACOMPTE: return ["Acompte de la", 'Commande'];
		default: return ['Issue de la', 'Pièce'];
	}
}

@Component({
	selector: 'app-quotationdetails',
	imports: [DatePipe, DecimalPipe, RouterLink, MatTooltipModule, MatProgressSpinnerModule, MatIconModule],
	templateUrl: './quotationdetails.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './quotationdetails.component.scss'
})
export class QuotationdetailsComponent {

	@Input() mouseover$!: Observable<HoveredOrder>;								// Mouse over a quotation line
	@Input() mouseout$!: Observable<void>;
	@Output() quotationShown = new EventEmitter<number>();									// Mouse out of a quotation line

	private mouseoversubscription!: Subscription;
	private mouseoutsubscription!: Subscription;
	private lifecyclesubscription!: Subscription;

	private lastmouseout: boolean = false;
	private reset$ = new Subject<void>();								// Coupe les lectures en vol : rien ne remplit l'aperçu après une nouvelle recherche

	public quotationId: number | null = null;
	public docRef: string | null = null;
	public lines: any[] = [];
	public deliveries: any[] = [];
	private slots: any[] = [];

	// Dossier de location survolé : son cycle (livré, facturé jusqu'au, prochaine échéance, factures filles), lu à part.
	public isRental = false;
	public parentOrderId: number | null = null;
	public parentLabel: [string, string] = parentOrderLabel(null);
	public lifecycle: RentalLifecycleView | null = null;
	public lifecycleLoading = false;
	public lifecycleError = '';
	private periodNames: Record<number, string> = {};

	// Provenance de la pièce survolée : « Caisse 1 (LVM La Seyne sur Mer) », soit le POSTE
	// et l'ÉTABLISSEMENT où elle a été saisie. La commande porte `lnk_station` (id, nom du
	// poste, id du magasin) mais PAS le nom du magasin : il faut le référentiel pour le
	// résoudre. Même libellé que la puce de l'écran commande (cf. orderStationLabel).
	public stationLabel = '';
	private orderStation: any = null;
	private stores: Store[] = [];


	constructor(private quotationsService: QuotationsService, private valuesService: ValuesService, private commonService: CommonService) { }

	ngOnInit(): void {

		this.valuesService.getValues$(constants.C_VALUES_DELIVERY_SLOT).subscribe((slots: any[]) => { this.slots = slots; });
		this.valuesService.getValues$(constants.C_VALUES_RENTAL_PERIOD).subscribe({ next: (periods: any[]) => { this.periodNames = Object.fromEntries((periods ?? []).map(p => [p.id, p.name])); } });

		// Le référentiel peut arriver après la première commande survolée : on recalcule.
		this.commonService.getStores().subscribe({ next: (stores: Store[]) => { this.stores = stores ?? []; this.refreshStationLabel(); } });

		this.mouseoutsubscription = this.mouseout$.subscribe(() => { this.lastmouseout = false; });

		const hovered$ = this.mouseover$.pipe(
			tap(() => { this.lastmouseout = true; }),
			debounceTime(300),
			filter(() => this.lastmouseout),
			filter((o) => o.id !== this.quotationId),						// la ligne déjà affichée n'est ni effacée ni relue (reset() remet quotationId à null)
			tap((o) => {
				this.quotationId = o.id;
				this.docRef = o.doc_ref ?? null;
				this.quotationShown.emit(o.id);
				this.isRental = o.doc_type === constants.DocType.LOCATION;
				this.parentOrderId = o.parent_order_id ?? null;
				this.parentLabel = parentOrderLabel(o.doc_type);
				this.lifecycle = null;
				this.lifecycleError = '';
				this.lifecycleLoading = this.isRental;
			}),
			share(),
		);

		// switchMap : la réponse d'une ligne quittée ne remplace jamais celle de la ligne affichée.
		this.mouseoversubscription = hovered$.pipe(
			switchMap((o) => this.quotationsService.getQuotationById(o.id).pipe(
				catchError((e) => { console.log("ERREUR Serveur : ", e.error); return of(null); }),
				takeUntil(this.reset$),
			)),
		).subscribe((items: any) => {
			if (!items?.length) return;
			this.lines = items[0].lines ?? [];
			this.deliveries = items[0].lnk_deliveries ?? [];
			this.orderStation = items[0].lnk_station ?? null;
			if (items[0]?.data?.doc_ref) {
				this.docRef = items[0].data.doc_ref;
			}
			this.refreshStationLabel();
		});

		this.lifecyclesubscription = hovered$.pipe(
			switchMap((o) => o.doc_type !== constants.DocType.LOCATION ? of(null) : this.quotationsService.getRentalLifecycle(o.id).pipe(
				map((view) => ({ view, error: '' })),
				catchError((e) => of({ view: null, error: typeof e?.error === 'string' ? e.error : 'Cycle de location illisible (erreur serveur).' })),
				takeUntil(this.reset$),
			)),
		).subscribe((r) => {
			if (!r) return;
			this.lifecycle = r.view;
			this.lifecycleError = r.error;
			this.lifecycleLoading = false;
		});
	}

	reset(): void { this.reset$.next(); this.lines = []; this.deliveries = []; this.quotationId = null; this.docRef = null; this.parentOrderId = null; this.orderStation = null; this.stationLabel = ''; this.isRental = false; this.lifecycle = null; this.lifecycleLoading = false; this.lifecycleError = ''; }

	/** « Poste (Établissement) », vide tant que la station ou son magasin sont inconnus. */
	private refreshStationLabel(): void {
		const station = this.orderStation;
		if (!station?.name) { this.stationLabel = ''; return; }
		const store = this.stores.find(s => s.id === station.store);
		this.stationLabel = store?.name ? `${station.name} (${store.name})` : station.name;
	}

	getDeliveryLines(delivery: any): any[] {  // permet d'obtenir a partir de la commande principale les infos des lignes de livraison card dans lnk_deliveryl on a uniquement une qty et une quotationl
		const ids = new Set((delivery.lnk_deliveryl ?? []).map((dl: any) => dl.id_order_l));
		return this.lines.filter((l: any) => ids.has(l.id));
	}

	getDeliveryModeLabel(mode: number): string {
		switch (mode) {
			case 1: return 'Emporté';
			case 2: return 'Emporté + tard';
			case 3: return 'Livraison immédiate';
			case 4: return 'Livraison planifiée';
			case constants.SALES_ORDER_MODE_CANCEL: return 'Annulation du reste à livrer';
			default: return 'Mode ' + mode;
		}
	}

	getSlotLabel(askslot: number): string {
		if (!askslot) return '';
		return this.slots.find(s => s.id == askslot)?.name ?? '';
	}

	// ── Cycle de location ────────────────────────────────────────────────────────

	/** Jour « aaaa-mm-jj » du serveur (déjà à l'heure de Paris) en jj/mm/aaaa, sans passer par Date ni par le fuseau du poste. */
	day(d: string | null | undefined): string {
		return d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '';
	}

	/** Jour de Paris (jj/mm/aaaa) d'un instant réel du BL (realdatdel), lu par businessDayOf. */
	blDay(at: string | null | undefined): string {
		return this.day(businessDayOf(at));
	}

	/** « du 01/09/2026 au 26/09/2026 » (start et end inclus), ou le seul jour quand la période tient dans une journée. */
	span(start: string | null, end: string | null): string {
		if (!start || !end) return '';
		return start === end ? `le ${this.day(start)}` : `du ${this.day(start)} au ${this.day(end)}`;
	}

	periodName(period: number | null): string {
		return period == null ? '' : (this.periodNames[period] ?? `période ${period}`);
	}

	get rentalLines(): RentalLineLifecycle[] { return this.lifecycle?.lines ?? []; }

	get reprise(): string {
		const r = this.lifecycle?.reprise;
		return r ? `Repris d'orthop · dossier ${r.dos_chrono ?? '?'}${r.dos_type ? ' (' + r.dos_type + ')' : ''}` : '';
	}

	statusLabel(l: RentalLineLifecycle): string {
		return statusLabel(l.status);
	}

	title(l: RentalLineLifecycle): string {
		return rentalTitle(l) ?? `Article ${l.product}`;
	}

	article(l: RentalLineLifecycle): string | null {
		return rentalSubtitle(l);
	}

	// Une ligne qui porte des anomalies les montre telles quelles, quel que soit son statut.
	statusHint(l: RentalLineLifecycle): string {
		const hint = statusHint(l.status);
		if (!l.anomalies?.length) return hint;
		const list = l.anomalies.join(' ; ');
		return l.status === 'blocked' ? list : `${hint} ⚠ ${list}`;
	}

	/** « rang / Durée », la Durée ≤ 0 étant illimitée. */
	progress(l: RentalLineLifecycle): string {
		const duration = Number(l.duration ?? 0);
		return `${l.rank} / ${duration > 0 ? duration : '∞'}`;
	}

	billedUntilHint(l: RentalLineLifecycle): string {
		switch (l.billed_until_source) {
			case 'mercalink': return `Dernière période des factures filles Mercalink (${l.billed_periods} période${l.billed_periods > 1 ? 's' : ''} facturée${l.billed_periods > 1 ? 's' : ''} par Mercalink).`;
			case 'orthop': return 'Dernier jour facturé par orthop avant la reprise.';
			case 'cycle': return 'Déduit du cycle de la ligne : aucune facture fille retrouvée.';
			default: return '';
		}
	}

	invoiceStateLabel(state: string): string {
		return constants.getOrderLabel(state);
	}

	/** Facture FSE (Axe 4) : pas de clic ni de navigation vers un devis/facture classique. */
	isFseInvoice(f: RentalChildInvoice): boolean {
		return !!f.doc_ref?.startsWith('FSE-') || f.doc_type === constants.DocType.FACTURE_FSE;
	}

	ngOnDestroy() {
		this.mouseoversubscription.unsubscribe();
		this.mouseoutsubscription.unsubscribe();
		this.lifecyclesubscription?.unsubscribe();
	}
}
